<script setup lang="ts">
// GGE's (i) explainer prose for the shared FundSignalPanel — moved verbatim
// out of GgeSignalPanel.vue (option C, 2026-10-11). Rendered inside the
// panel's .explainer box, which owns the styling.
import { computed } from 'vue'
import { bps, fmtUsd, type FundSignalView } from './fundSignalPanel'
import { ggeClPrint } from './fundSignalFields'

const props = defineProps<{ section: string; v: FundSignalView }>()

const clPrint = computed(() => ggeClPrint(props.v.signal))

const mVerdictText = computed(() => {
  const { mBps, thresholdBps } = props.v
  if (mBps == null || thresholdBps == null) return null
  return mBps >= thresholdBps
    ? `at/above the ${thresholdBps.toFixed(1)} bps threshold — momentum passes`
    : `below the ${thresholdBps.toFixed(1)} bps threshold — no BUY today`
})
</script>

<template>
  <section v-if="section === 'm0840'">
    <p class="info-now"><strong>Now:</strong>
      m<sub>08:40</sub> = <code>{{ bps(v.mBps) }}</code><span v-if="mVerdictText">, {{ mVerdictText }}</span>.
      <span v-if="v.thresholdReasonText">Threshold: {{ v.thresholdReasonText }}.</span>
    </p>
    <p><strong>m<sub>08:40</sub> — CL momentum into the decision</strong></p>
    <p>The return of the CL (WTI crude) futures front month from the prior
      session's 16:00 America/New York close (walking back up to 5
      sessions across holidays) to the last print before the 08:40 London
      decision. <strong>BUY requires m ≥ threshold.</strong> The fund's
      NAV strikes at 12:00 UK with ~65% of the book valued at T−1
      North-American closes — m<sub>08:40</sub> measures exactly the
      energy-price information that strike has not yet absorbed. A CL
      print older than the staleness cap degrades the day to HOLD rather
      than trading on old data. There is no yield gate: the validated
      tune trades on momentum alone.</p>
  </section>
  <section v-else-if="section === 'clprint'">
    <p class="info-now"><strong>Now:</strong>
      CL print <code>{{ fmtUsd(clPrint?.price) }}</code> vs anchor
      <code>{{ fmtUsd(clPrint?.anchor) }}</code>.
    </p>
    <p><strong>CL print — the two legs of the momentum arithmetic</strong></p>
    <p>The decision print is the last CL 1-minute bar at/before 08:40
      London; the anchor is the prior session's 16:00 New York close.
      m<sub>08:40</sub> = print/anchor − 1. Both are shown so the
      operator can check the arithmetic against a broker quote when the
      decision looks surprising.</p>
  </section>
  <section v-else-if="section === 'decision'">
    <p class="info-now"><strong>Now:</strong>
      <code>{{ v.decisionLabel }}</code><span v-if="v.decisionReasonText"> — {{ v.decisionReasonText }}</span>.
    </p>
    <p><strong>Decision — published once per UK business day</strong></p>
    <p>The producer fires at <strong>08:40 Europe/London</strong> (same
      instant as BGA/JH/GOLD), 20 minutes ahead of the <strong>09:00
      London HL dealing cutoff</strong> (verified against HL). Orders in
      before the cutoff price at the fund's <strong>12:00 UK
      valuation</strong>. <code>BUY</code> = momentum ≥
      threshold; anything else is <code>SELL</code> (full liquidation per
      the dealing-plan canon). A degraded day publishes <code>HOLD</code>
      and carries the prior position forward via the position proxy —
      never a fresh trade off stale data.</p>
  </section>
</template>
